import RepeatIcon from "@mui/icons-material/Repeat";
import { Box, Collapse, IconButton } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { type DropdownItem, SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { CONSECUTIVE_PERIOD_MAX, CONSECUTIVE_PERIOD_MIN, type ConsecutivePeriod } from "./consts";

/** Numeric options (CONSECUTIVE_PERIOD_MIN..MAX) shared by both period dropdowns. */
const periodAmountItems: Array<DropdownItem<number>> = Array.from({ length: CONSECUTIVE_PERIOD_MAX - CONSECUTIVE_PERIOD_MIN + 1 }, (_, i) => {
	const value = i + CONSECUTIVE_PERIOD_MIN;
	return { label: `${value}`, value, id: `consecutive-period-${value}` };
});

type Props = {
	period: ConsecutivePeriod;
	onChange: (period: ConsecutivePeriod) => void;
};

/**
 * "Consecutive period" control: hidden behind an icon button; clicking it reveals two dropdowns
 * (working days on / rest days off) with a horizontal expand animation. Defines the rotation the
 * Consecutive Master Schedule renders. Mirrors the recurrence editor's working/rest day selectors.
 */
export const ConsecutivePeriodInput: FC<Props> = ({ period, onChange }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const opaqueWhenOpen = useDebouncedValue(open, 200);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }} data-testid="consecutive-master-schedule-period">
			<Collapse in={open} orientation="horizontal">
				{/* nowrap keeps the dropdowns on one line while the width animates from 0 → content;
				    opacity fades in after the expand and out immediately on close. */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						pl: 1,
						whiteSpace: "nowrap",
						opacity: open && opaqueWhenOpen ? 1 : 0,
						transition: "opacity 200ms ease",
					}}
				>
					<SystemDropdown
						label={t("working.days")}
						items={periodAmountItems}
						selectedItem={period.workingDays}
						onChange={(value) => value !== undefined && onChange({ ...period, workingDays: value })}
						formControlSx={{ width: "110px" }}
						data-testid="consecutive-master-schedule-period-working"
					/>
					<SystemDropdown
						label={t("rest.days")}
						items={periodAmountItems}
						selectedItem={period.restDays}
						onChange={(value) => value !== undefined && onChange({ ...period, restDays: value })}
						formControlSx={{ width: "110px" }}
						data-testid="consecutive-master-schedule-period-rest"
					/>
				</Box>
			</Collapse>
			<InHouseTooltip title={t("consecutive.period")}>
				<IconButton
					size="small"
					onClick={() => setOpen((prev) => !prev)}
					aria-label={t("consecutive.period")}
					aria-expanded={open}
					data-testid="consecutive-master-schedule-period-toggle"
				>
					<RepeatIcon fontSize="small" />
				</IconButton>
			</InHouseTooltip>
		</Box>
	);
};
